import { List } from 'immutable';
import settings from '../settings';
import Service, { SetStateFunction } from "./Service";
import LoggingService, { Severity } from './LoggingService';
import MQTTService, { MessageReceived, Subscription } from './MQTTService';
import { WebRTCInboxPayload } from './MQTTService/payloads';

interface ServiceProxy {
    logging_service: LoggingService;
    mqtt_service: MQTTService;
    peer_client_id: string;
    peer_connection: RTCPeerConnection;
    get_state: () => ConnectionState;
    set_state: SetStateFunction<ConnectionState>;
}

abstract class AbstractState {
    public abstract name: string;
    public abstract handle_incoming_description(service: ServiceProxy, description: RTCSessionDescriptionInit): void;
    public abstract handle_incoming_candidate(service: ServiceProxy, candidate: RTCIceCandidateInit): void;
    public abstract handle_description_accepted(service: ServiceProxy): void;
}

interface NewAwaitingAnswerInput {
    candidates: List<RTCIceCandidateInit>;
}

class AwaitingOffer extends AbstractState {
    public name = "awaiting_offer";
    private readonly candidates: List<RTCIceCandidateInit>;

    constructor(candidates: List<RTCIceCandidateInit>) {
        super();
        this.candidates = candidates;
    }

    public handle_incoming_description = (service: ServiceProxy, description: RTCSessionDescriptionInit) => {
        if (description.type !== "offer") throw new Error(`Awaiting offer but received description of type ${description.type}`);
        service.set_state(new AcceptingDescription(this.candidates));
        this.accept_offer(service, description).then(() => service.get_state().handle_description_accepted(service));
    }

    private accept_offer = async (service: ServiceProxy, description: RTCSessionDescriptionInit) => {
        await service.peer_connection.setRemoteDescription(description)
        const answer = await service.peer_connection.createAnswer();
        service.mqtt_service.publish_webrtc_description(service.peer_client_id, answer);
    }

    public handle_incoming_candidate = (service: ServiceProxy, candidate: RTCIceCandidateInit) => {
        service.set_state(new AwaitingOffer(this.candidates.push(candidate)));
    }

    public handle_description_accepted = (service: ServiceProxy) => {
        service.logging_service.log({
            severity: Severity.Informational,
            message: "handle_description_accepted called while in AwaitingOffer state",
        });
    }
}

class AwaitingAnswer extends AbstractState {
    public name = "awaiting_answer";
    private readonly candidates: List<RTCIceCandidateInit>;

    constructor(input: NewAwaitingAnswerInput) {
        super();
        this.candidates = input.candidates;
    }

    public handle_incoming_description = (service: ServiceProxy, description: RTCSessionDescriptionInit): void => {
        if (description.type !== "answer") throw new Error(`Awaiting answer but received description of type ${description.type}`);
        service.set_state(new AcceptingDescription(this.candidates));
        service.peer_connection.setRemoteDescription(description).then(() => service.get_state().handle_description_accepted(service));
    }

    public handle_incoming_candidate = (service: ServiceProxy, candidate: RTCIceCandidateInit): void => {
        service.set_state(new AwaitingAnswer({
            candidates: this.candidates.push(candidate),
        }));
    }

    public handle_description_accepted = (service: ServiceProxy): void => {
        service.logging_service.log({
            severity: Severity.Informational,
            message: "handle_description_accepted called while in AwaitingAnswer state",
        });
    }
}

class AcceptingDescription extends AbstractState {
    public name = "accepting_description";
    private readonly candidates: List<RTCIceCandidateInit>;

    constructor(candidates: List<RTCIceCandidateInit>) {
        super();
        this.candidates = candidates;
    }

    public handle_incoming_description = (service: ServiceProxy, description: RTCSessionDescriptionInit): void => {
        // TODO duplicate signals are possible. is the description the same? if so we can ignore it
        service.logging_service.log({
            severity: Severity.Informational,
            message: `handle_incoming_description called while in AcceptingDescription state with description of type ${description.type}`,
        })
    }

    public handle_incoming_candidate = (service: ServiceProxy, candidate: RTCIceCandidateInit): void => {
        service.set_state(new AcceptingDescription(this.candidates.push(candidate)));
    }

    public handle_description_accepted = (service: ServiceProxy): void => {
        service.set_state(new Active());
        this.candidates.forEach((candidate) => service.peer_connection.addIceCandidate(candidate));
    }
}

class Active extends AbstractState {
    public name = "active";

    public handle_incoming_description = (service: ServiceProxy, description: RTCSessionDescriptionInit): void => {
        service.logging_service.log({
            severity: Severity.Informational,
            message: `handle_incoming_description called while in Active state with description of type ${description.type}`,
        });
    }

    public handle_incoming_candidate = (service: ServiceProxy, candidate: RTCIceCandidateInit): void => {
        service.peer_connection.addIceCandidate(candidate);
    }

    public handle_description_accepted = (service: ServiceProxy): void => {
        service.logging_service.log({
            severity: Severity.Informational,
            message: "handle_description_accepted called while in Active state",
        });
    }
}

type ConnectionState = AwaitingOffer | AwaitingAnswer | AcceptingDescription | Active;

interface NewConnectionInput {
    logging_service: LoggingService;
    mqtt_service: MQTTService;
    peer_client_id: string;
    peer_connection: RTCPeerConnection;
    initial_state: ConnectionState;
}

interface InitiateInput {
    logging_service: LoggingService;
    mqtt_service: MQTTService;
    peer_client_id: string;
}

interface AcceptInput {
    logging_service: LoggingService;
    mqtt_service: MQTTService;
    peer_client_id: string;
    offer: RTCSessionDescriptionInit;
}

class Connection extends Service<ConnectionState> {
    protected readonly name = "Connection";
    private readonly mqtt_service: MQTTService;
    private readonly peer_client_id: string;
    public readonly peer_connection: RTCPeerConnection;
    private readonly subscription: Subscription;
    private readonly proxy: ServiceProxy;

    private constructor(input: NewConnectionInput) {
        super({
            logging_service: input.logging_service,
            initial_state: input.initial_state,
        });
        this.mqtt_service = input.mqtt_service;
        this.peer_client_id = input.peer_client_id;
        this.peer_connection = input.peer_connection;
        this.subscription = input.mqtt_service.subscribe_to_webrtc_inbox(this.handle_webrtc_message);
        this.proxy = {
            logging_service: this.logging_service,
            mqtt_service: this.mqtt_service,
            peer_client_id: this.peer_client_id,
            peer_connection: input.peer_connection,
            get_state: this.get_state,
            set_state: this.set_state,
        };
        input.peer_connection.addEventListener("icecandidate", this.handle_icecandidate);
    }

    protected to_string = (state: ConnectionState): string => {
        return state.name;
    }

    private handle_webrtc_message = (message: MessageReceived): void => {
        const payload = JSON.parse(message.payload) as WebRTCInboxPayload;
        if (payload.from_client_id !== this.peer_client_id) return;
        const state = this.get_state();
        switch (payload.type) {
            case "description":
                state.handle_incoming_description(this.proxy, payload.description);
                return;
            case "candidate":
                state.handle_incoming_candidate(this.proxy, payload.candidate);
                return;
            default:
                const _exhaustive_check: never = payload;
                throw new TypeError(`unhandled WebRTC payload: ${_exhaustive_check}`);
        }
    }

    private handle_icecandidate = (event: RTCPeerConnectionIceEvent): void => {
        // TODO null indicates end of candidates. Should we do anything with that?
        if (event.candidate === null) return;
        this.mqtt_service.publish_webrtc_candidate(this.peer_client_id, event.candidate);
    }

    public close = (): void => {
        this.subscription.close();
        this.peer_connection.removeEventListener("icecandidate", this.handle_icecandidate);
        this.peer_connection.close();
    }

    static initiate = (input: InitiateInput): Connection => {
        const peer_connection = new RTCPeerConnection(settings.RTC);
        // These transceivers are specifically for the parent station. 
        // When the baby starts initiating the connection it will use "sendonly" for video and audio.
        peer_connection.addTransceiver("video", { direction: "recvonly" });
        // When talkback is implemented audio will be changed to "sendrecv".
        peer_connection.addTransceiver("audio", { direction: "recvonly" });

        peer_connection.setLocalDescription().then(() => {
            if (peer_connection.localDescription === null) throw new Error("missing local description");
            input.mqtt_service.publish_webrtc_description(input.peer_client_id, peer_connection.localDescription);
        });
        return new Connection({
            logging_service: input.logging_service,
            mqtt_service: input.mqtt_service,
            peer_client_id: input.peer_client_id,
            peer_connection,
            initial_state: new AwaitingAnswer({
                candidates: List<RTCIceCandidateInit>(),
            }),
        });
    }

    static accept_offer = (input: AcceptInput): Connection => {
        const peer_connection = new RTCPeerConnection(settings.RTC);
        const promise = peer_connection.setRemoteDescription(input.offer)
            .then(() => peer_connection.createAnswer())
            .then(async (answer) => {
                await peer_connection.setLocalDescription(answer);
                input.mqtt_service.publish_webrtc_description(input.peer_client_id, answer);
            });

        const connection = new Connection({
            logging_service: input.logging_service,
            mqtt_service: input.mqtt_service,
            peer_connection,
            peer_client_id: input.peer_client_id,
            initial_state: new AcceptingDescription(List<RTCIceCandidateInit>()),
        });

        promise.then(() => connection.get_state().handle_description_accepted(connection.proxy));

        return connection;
    }
}

export default Connection;
